import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  IconButton,
  CircularProgress,
  Checkbox,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material'
import { supabase } from '../../config/supabase'

/* eslint-disable @typescript-eslint/no-unused-vars */
// Google Maps type declarations (used at runtime via window.google)
declare global {
  interface Window {
    google?: {
      maps: {
        places: {
          Autocomplete: new (
            input: HTMLInputElement,
            options?: google.maps.places.AutocompleteOptions
          ) => google.maps.places.Autocomplete
        }
        Geocoder: new () => google.maps.Geocoder
      }
    }
  }
}

declare namespace google.maps {
  interface LatLng {
    lat(): number
    lng(): number
  }
  interface GeocoderResult {
    formatted_address: string
    geometry: {
      location: LatLng
    }
  }
  interface GeocoderResponse {
    results: GeocoderResult[]
  }
  class Geocoder {
    geocode(
      request: { address: string; componentRestrictions?: { country: string } }
    ): Promise<GeocoderResponse>
  }
  namespace places {
    interface AutocompleteOptions {
      componentRestrictions?: { country: string }
      fields?: string[]
    }
    interface PlaceResult {
      formatted_address?: string
      geometry?: {
        location?: LatLng
      }
    }
    class Autocomplete {
      constructor(input: HTMLInputElement, options?: AutocompleteOptions)
      addListener(event: string, handler: () => void): void
      getPlace(): PlaceResult
    }
  }
}
/* eslint-enable @typescript-eslint/no-unused-vars */

// Google Maps API key (same as used elsewhere in the app)
const GOOGLE_MAPS_API_KEY = 'AIzaSyB0OWsYcdAdMN8mjSLC4GIBVm_kOgXN6bA'

// Price input field helper component (defined outside to prevent re-creation on each render)
const PriceField = ({ 
  label, 
  value, 
  onChange, 
  help,
  placeholder = '0',
  readOnly = false,
  required = false
}: { 
  label: string
  value: number | null | undefined
  onChange: (value: number | null) => void
  help?: string
  placeholder?: string
  readOnly?: boolean
  required?: boolean
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
    <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
      {label}
      {required && <Typography component="span" color="error"> *</Typography>}
      {help && (
        <Tooltip title={help} arrow>
          <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
            <HelpIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      )}
    </Typography>
    <TextField
      size="small"
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
      disabled={readOnly}
      placeholder={placeholder}
      required={required}
      error={required && (value === null || value === undefined || value === 0)}
      InputProps={{
        endAdornment: <InputAdornment position="end">kr.</InputAdornment>,
      }}
      sx={{ width: 150 }}
    />
  </Box>
)

interface PrisberegnerPricingData {
  // Base package prices
  base_price_cremation_ceremony?: number | null
  base_price_cremation_urn_ceremony?: number | null
  base_price_cremation_no_ceremony?: number | null
  base_price_burial_ceremony?: number | null
  base_price_burial_no_ceremony?: number | null
  
  // What's included in base price (NEW)
  coffin_included_in_base: boolean
  urn_included_in_base: boolean
  driving_included_in_base: boolean
  
  // Driving
  driving_from_address?: string | null
  driving_from_coordinates?: { lat: number; lng: number } | null
  driving_model: 'fixed_per_trip' | 'per_km'
  driving_trips_count: number
  driving_base_price?: number | null
  driving_included_km?: number | null
  driving_extra_km_price?: number | null
  driving_per_km_price?: number | null
  driving_minimum_price?: number | null
  driving_weekend_surcharge: number  // Weekend/helligdagstillæg for kørsel
  driving_night_surcharge: number    // Nattetillæg for kørsel
  
  // Pickup surcharges
  pickup_home_surcharge: number
  pickup_hospital_surcharge: number
  pickup_institution_surcharge: number
  
  // Special conditions
  overweight_surcharge: number
  tall_surcharge: number
  
  // Products - simplified to single price (used if NOT included in base)
  coffin_price: number
  urn_price: number
  
  // Additional fees
  honorar_fee: number
  preparation_fee: number
  hygiene_fee: number
  weekend_surcharge: number
  
  // Package description
  package_includes?: string | null
  package_excludes?: string | null
  
  // Boost settings (admin only)
  boost_in_quotes: boolean
  boost_priority: number
}

interface PrisberegnerFormProps {
  undertakerId?: string
  chainId?: string
  readOnly?: boolean
  isAdmin?: boolean
  onSave?: (data: PrisberegnerPricingData) => void
}

export interface PrisberegnerFormHandle {
  save: () => Promise<boolean>
  getData: () => PrisberegnerPricingData
}

const defaultData: PrisberegnerPricingData = {
  base_price_cremation_ceremony: null,
  base_price_cremation_urn_ceremony: null,
  base_price_cremation_no_ceremony: null,
  base_price_burial_ceremony: null,
  base_price_burial_no_ceremony: null,
  // Default: coffin, urn, and driving are INCLUDED in base price (common for Danish undertakers)
  coffin_included_in_base: true,
  urn_included_in_base: true,
  driving_included_in_base: true,
  driving_from_address: null,
  driving_from_coordinates: null,
  driving_model: 'fixed_per_trip',
  driving_trips_count: 2,
  driving_base_price: null,
  driving_included_km: 15,
  driving_extra_km_price: null,
  driving_per_km_price: null,
  driving_minimum_price: null,
  driving_weekend_surcharge: 0,
  driving_night_surcharge: 0,
  pickup_home_surcharge: 0,
  pickup_hospital_surcharge: 0,
  pickup_institution_surcharge: 0,
  overweight_surcharge: 0,
  tall_surcharge: 0,
  coffin_price: 0,
  urn_price: 0,
  honorar_fee: 0,
  preparation_fee: 0,
  hygiene_fee: 0,
  weekend_surcharge: 0,
  package_includes: null,
  package_excludes: null,
  boost_in_quotes: false,
  boost_priority: 0,
}

const PrisberegnerForm = forwardRef<PrisberegnerFormHandle, PrisberegnerFormProps>(({ 
  undertakerId, 
  chainId, 
  readOnly = false,
  isAdmin = false,
  onSave
}, ref) => {
  const [data, setData] = useState<PrisberegnerPricingData>(defaultData)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pricingId, setPricingId] = useState<string | null>(null)
  const [addressValidated, setAddressValidated] = useState<boolean | null>(null)
  const [addressValidating, setAddressValidating] = useState(false)
  const addressInputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  // Load Google Maps script
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.google) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    }
  }, [])

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (addressInputRef.current && window.google && !autocompleteRef.current) {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            componentRestrictions: { country: 'dk' },
            fields: ['formatted_address', 'geometry'],
          }
        )

        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current?.getPlace()
          if (place?.formatted_address && place?.geometry?.location) {
            setData(prev => ({
              ...prev,
              driving_from_address: place.formatted_address || null,
              driving_from_coordinates: {
                lat: place.geometry!.location!.lat(),
                lng: place.geometry!.location!.lng(),
              }
            }))
            setAddressValidated(true)
          } else {
            setAddressValidated(false)
          }
        })
      }
    }

    // Wait for Google Maps to load
    const checkGoogle = setInterval(() => {
      if (window.google) {
        initAutocomplete()
        clearInterval(checkGoogle)
      }
    }, 100)

    return () => clearInterval(checkGoogle)
  }, [])

  // Load existing pricing data
  useEffect(() => {
    const loadPricing = async () => {
      if (!undertakerId && !chainId) {
        setLoading(false)
        return
      }

      try {
        // Select only the columns we need, excluding auth.users foreign keys
        const columns = `
          id,
          undertaker_id,
          chain_id,
          enabled,
          base_price_cremation_ceremony,
          base_price_cremation_urn_ceremony,
          base_price_cremation_no_ceremony,
          base_price_burial_ceremony,
          base_price_burial_no_ceremony,
          coffin_included_in_base,
          urn_included_in_base,
          driving_included_in_base,
          driving_from_address,
          driving_from_coordinates,
          driving_model,
          driving_trips_count,
          driving_base_price,
          driving_included_km,
          driving_extra_km_price,
          driving_per_km_price,
          driving_minimum_price,
          driving_weekend_surcharge,
          driving_night_surcharge,
          pickup_home_surcharge,
          pickup_hospital_surcharge,
          pickup_institution_surcharge,
          overweight_surcharge,
          tall_surcharge,
          coffin_price,
          urn_price,
          coffin_prices,
          urn_prices,
          honorar_fee,
          preparation_fee,
          hygiene_fee,
          weekend_surcharge,
          package_includes,
          package_excludes,
          boost_in_quotes,
          boost_priority,
          last_updated_at
        `
        
        let query = supabase.from('prisberegner_pricing').select(columns)
        
        if (undertakerId) {
          query = query.eq('undertaker_id', undertakerId)
        } else if (chainId) {
          query = query.eq('chain_id', chainId)
        }

        const { data: pricing, error } = await query.single()

        if (error && error.code !== 'PGRST116') {
          throw error
        }

        if (pricing) {
          setPricingId(pricing.id)
          // Handle legacy coffin_prices/urn_prices format
          const coffinPrice = pricing.coffin_price ?? pricing.coffin_prices?.standard ?? 0
          const urnPrice = pricing.urn_price ?? pricing.urn_prices?.standard ?? 0
          
          setData({
            ...defaultData,
            ...pricing,
            coffin_price: coffinPrice,
            urn_price: urnPrice,
            // Default to true for backward compatibility (most packages include these)
            coffin_included_in_base: pricing.coffin_included_in_base ?? true,
            urn_included_in_base: pricing.urn_included_in_base ?? true,
            driving_included_in_base: pricing.driving_included_in_base ?? true,
          })
          
          // If address exists, mark as validated
          if (pricing.driving_from_address && pricing.driving_from_coordinates) {
            setAddressValidated(true)
          }
        }
      } catch (err: any) {
        console.error('Error loading prisberegner pricing:', err)
        setError('Kunne ikke indlæse prisberegner data')
      } finally {
        setLoading(false)
      }
    }

    loadPricing()
  }, [undertakerId, chainId])

  // Update field value
  const updateField = (field: keyof PrisberegnerPricingData, value: any) => {
    setData(prev => ({ ...prev, [field]: value }))
  }

  // Handle address change (manual typing)
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newAddress = e.target.value
    setData(prev => ({ 
      ...prev, 
      driving_from_address: newAddress || null,
      driving_from_coordinates: null // Clear coordinates when manually typing
    }))
    setAddressValidated(null) // Reset validation state
  }

  // Validate address manually (if user types without selecting from autocomplete)
  const validateAddress = async () => {
    if (!data.driving_from_address || data.driving_from_coordinates) return
    if (!window.google) return // Google Maps not loaded yet
    
    setAddressValidating(true)
    try {
      const geocoder = new window.google.maps.Geocoder()
      const result = await geocoder.geocode({ 
        address: data.driving_from_address,
        componentRestrictions: { country: 'dk' }
      })
      
      if (result.results && result.results.length > 0) {
        const location = result.results[0].geometry.location
        setData(prev => ({
          ...prev,
          driving_from_address: result.results[0].formatted_address,
          driving_from_coordinates: {
            lat: location.lat(),
            lng: location.lng(),
          }
        }))
        setAddressValidated(true)
      } else {
        setAddressValidated(false)
      }
    } catch (err) {
      console.error('Address validation error:', err)
      setAddressValidated(false)
    } finally {
      setAddressValidating(false)
    }
  }

  // Save pricing data
  const handleSave = async (): Promise<boolean> => {
    if (!undertakerId && !chainId) return false
    
    setError(null)

    try {
      // Prepare save data - only include fields we want to save
      // Explicitly exclude: id, enabled_by, last_updated_by, enabled_at, created_at, updated_at
      const saveData = {
        // Link to entity
        undertaker_id: undertakerId || null,
        chain_id: chainId || null,
        
        // Status
        enabled: true,
        
        // Base prices
        base_price_cremation_ceremony: data.base_price_cremation_ceremony,
        base_price_cremation_urn_ceremony: data.base_price_cremation_urn_ceremony,
        base_price_cremation_no_ceremony: data.base_price_cremation_no_ceremony,
        base_price_burial_ceremony: data.base_price_burial_ceremony,
        base_price_burial_no_ceremony: data.base_price_burial_no_ceremony,
        
        // What's included in base price
        coffin_included_in_base: data.coffin_included_in_base,
        urn_included_in_base: data.urn_included_in_base,
        driving_included_in_base: data.driving_included_in_base,
        
        // Driving
        driving_from_address: data.driving_from_address,
        driving_from_coordinates: data.driving_from_coordinates,
        driving_model: data.driving_model,
        driving_trips_count: data.driving_trips_count,
        driving_base_price: data.driving_base_price,
        driving_included_km: data.driving_included_km,
        driving_extra_km_price: data.driving_extra_km_price,
        driving_per_km_price: data.driving_per_km_price,
        driving_minimum_price: data.driving_minimum_price,
        driving_weekend_surcharge: data.driving_weekend_surcharge,
        driving_night_surcharge: data.driving_night_surcharge,
        
        // Surcharges
        pickup_home_surcharge: data.pickup_home_surcharge,
        pickup_hospital_surcharge: data.pickup_hospital_surcharge,
        pickup_institution_surcharge: data.pickup_institution_surcharge,
        overweight_surcharge: data.overweight_surcharge,
        tall_surcharge: data.tall_surcharge,
        
        // Products (both formats) - used if NOT included in base
        coffin_price: data.coffin_price,
        urn_price: data.urn_price,
        coffin_prices: { standard: data.coffin_price },
        urn_prices: { standard: data.urn_price },
        
        // Fees
        honorar_fee: data.honorar_fee,
        preparation_fee: data.preparation_fee,
        hygiene_fee: data.hygiene_fee,
        weekend_surcharge: data.weekend_surcharge,
        
        // Package info
        package_includes: data.package_includes,
        package_excludes: data.package_excludes,
        
        // Boost (admin only)
        boost_in_quotes: data.boost_in_quotes,
        boost_priority: data.boost_priority,
        
        // Timestamp
        last_updated_at: new Date().toISOString(),
      }

      if (pricingId) {
        const { error } = await supabase
          .from('prisberegner_pricing')
          .update(saveData)
          .eq('id', pricingId)

        if (error) throw error
      } else {
        const { data: newPricing, error } = await supabase
          .from('prisberegner_pricing')
          .insert([saveData])
          .select('id')
          .single()

        if (error) throw error
        if (newPricing) setPricingId(newPricing.id)
      }

      onSave?.(data)
      return true
    } catch (err: any) {
      console.error('Error saving prisberegner pricing:', err)
      setError('Kunne ikke gemme prisberegner data: ' + err.message)
      return false
    }
  }

  // Expose save method to parent via ref
  useImperativeHandle(ref, () => ({
    save: handleSave,
    getData: () => data
  }))

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Info banner */}
      <Alert severity="info" sx={{ mb: 1 }}>
        <strong>Prisberegner indstillinger</strong>
        <br />
        Disse priser bruges KUN til prisberegneren og er helt adskilt fra "Services" fanen.
        Dækningsområde bestemmes af dit eksisterende Serviceområde.
      </Alert>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Section: Base Package Prices */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Grundpakker (inkl. moms)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Angiv den samlede pris for hver pakketype. Vælg nedenfor hvad der er inkluderet i grundprisen.
          </Typography>
          
          {/* What's included checkboxes */}
          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'primary.50', borderColor: 'primary.200' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Hvad er inkluderet i grundprisen?
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.coffin_included_in_base}
                  onChange={(e) => updateField('coffin_included_in_base', e.target.checked)}
                  disabled={readOnly}
                  size="small"
                />
              }
              label={<Typography variant="body2">Kiste inkluderet</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.urn_included_in_base}
                  onChange={(e) => updateField('urn_included_in_base', e.target.checked)}
                  disabled={readOnly}
                  size="small"
                />
              }
              label={<Typography variant="body2">Urne inkluderet (ved bisættelse)</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.driving_included_in_base}
                  onChange={(e) => updateField('driving_included_in_base', e.target.checked)}
                  disabled={readOnly}
                  size="small"
                />
              }
              label={<Typography variant="body2">Standardkørsel inkluderet (kun ekstra km beregnes)</Typography>}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Hvis markeret, lægges disse IKKE oveni grundprisen. Kun ekstra km ud over inkluderede km beregnes.
            </Typography>
          </Paper>
          
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Bisættelse (kremering)
          </Typography>
          <PriceField 
            label="Med traditionel ceremoni (2 kørsler)"
            value={data.base_price_cremation_ceremony}
            onChange={(v) => updateField('base_price_cremation_ceremony', v)}
            help="Kisten er tilstede ved ceremonien, derefter til krematoriet. 2 kørsler inkluderet."
            readOnly={readOnly}
          />
          <PriceField 
            label="Med ceremoni over urne (1 kørsel)"
            value={data.base_price_cremation_urn_ceremony}
            onChange={(v) => updateField('base_price_cremation_urn_ceremony', v)}
            help="Kun afhentning til krematoriet. Ceremoni sker bagefter med urne. 1 kørsel inkluderet."
            readOnly={readOnly}
          />
          <PriceField 
            label="Uden ceremoni (1 kørsel)"
            value={data.base_price_cremation_no_ceremony}
            onChange={(v) => updateField('base_price_cremation_no_ceremony', v)}
            help="Kun afhentning direkte til krematoriet. 1 kørsel inkluderet."
            readOnly={readOnly}
          />

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
            Begravelse
          </Typography>
          <PriceField 
            label="Med ceremoni (2 kørsler)"
            value={data.base_price_burial_ceremony}
            onChange={(v) => updateField('base_price_burial_ceremony', v)}
            help="Afhentning og transport til ceremoni/kirkegård. 2 kørsler inkluderet."
            readOnly={readOnly}
          />
          <PriceField 
            label="Uden ceremoni (1 kørsel)"
            value={data.base_price_burial_no_ceremony}
            onChange={(v) => updateField('base_price_burial_no_ceremony', v)}
            help="Kun afhentning til kirkegård. 1 kørsel inkluderet."
            readOnly={readOnly}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section: Driving */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Rustvognskørsel
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* Info about how driving is calculated */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Antal kørsler bestemmes af pakketypen:
            </Typography>
            <Typography variant="body2">
              • <strong>Med ceremoni</strong> = 2 kørsler (afhentning → ceremoni → krematorium/kirkegård)
            </Typography>
            <Typography variant="body2">
              • <strong>Uden ceremoni</strong> = 1 kørsel (afhentning → krematorium/kirkegård)
            </Typography>
            {data.driving_included_in_base && (
              <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                ✓ Standardkørsel er inkluderet i grundpakken – kun ekstra km beregnes.
              </Typography>
            )}
          </Alert>

          {/* Override address with Google Places validation */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Alternativ kørselsadresse (valgfrit)"
              inputRef={addressInputRef}
              value={data.driving_from_address || ''}
              onChange={handleAddressChange}
              onBlur={validateAddress}
              disabled={readOnly}
              placeholder="Begynd at skrive og vælg fra listen"
              helperText={
                addressValidated === true 
                  ? "Adresse verificeret" 
                  : addressValidated === false 
                    ? "Kunne ikke verificere adressen - vælg fra listen"
                    : "Lad tom for at bruge din hovedadresse. Vælg fra listen for at verificere."
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {addressValidating ? (
                      <CircularProgress size={20} />
                    ) : addressValidated === true ? (
                      <CheckCircleIcon color="success" />
                    ) : addressValidated === false ? (
                      <ErrorIcon color="error" />
                    ) : null}
                  </InputAdornment>
                ),
              }}
              error={addressValidated === false}
            />
          </Box>

          {/* Pricing model */}
          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Prismodel for ekstra km</Typography>
            <RadioGroup
              value={data.driving_model}
              onChange={(e) => updateField('driving_model', e.target.value)}
            >
              <FormControlLabel 
                value="fixed_per_trip" 
                control={<Radio size="small" />} 
                label="Fast pris pr. kørsel (med inkluderede km)"
                disabled={readOnly}
              />
              <FormControlLabel 
                value="per_km" 
                control={<Radio size="small" />} 
                label="Pris pr. kilometer"
                disabled={readOnly}
              />
            </RadioGroup>
          </FormControl>

          {data.driving_model === 'fixed_per_trip' ? (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Fast pris model</Typography>
              <PriceField 
                label="Grundpris pr. ekstra kørsel"
                value={data.driving_base_price}
                onChange={(v) => updateField('driving_base_price', v)}
                help={data.driving_included_in_base 
                  ? "Bruges kun hvis kunden vælger ekstra kørsel ud over det inkluderede"
                  : "Grundpris pr. kørsel (lægges oveni grundpakken)"}
                readOnly={readOnly}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 200 }}>
                  Inkluderede kilometer pr. tur
                  <Tooltip title="Antal km der er inkluderet i hver kørsel. Kørsel ud over dette beregnes som ekstra km." arrow>
                    <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                      <HelpIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={data.driving_included_km ?? ''}
                  onChange={(e) => updateField('driving_included_km', e.target.value ? Number(e.target.value) : null)}
                  disabled={readOnly}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">km</InputAdornment>,
                  }}
                  sx={{ width: 150 }}
                />
              </Box>
              <PriceField 
                label="Pris pr. ekstra km"
                value={data.driving_extra_km_price}
                onChange={(v) => updateField('driving_extra_km_price', v)}
                help="Pris for hver km der overskrider de inkluderede km"
                readOnly={readOnly}
              />
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Pris pr. km model</Typography>
              <PriceField 
                label="Pris pr. kilometer"
                value={data.driving_per_km_price}
                onChange={(v) => updateField('driving_per_km_price', v)}
                readOnly={readOnly}
              />
              <PriceField 
                label="Minimumspris pr. tur"
                value={data.driving_minimum_price}
                onChange={(v) => updateField('driving_minimum_price', v)}
                readOnly={readOnly}
              />
            </Paper>
          )}

          {/* Surcharges for driving */}
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Tillæg for kørsel</Typography>
            <PriceField 
              label="Weekend/helligdagstillæg"
              value={data.driving_weekend_surcharge}
              onChange={(v) => updateField('driving_weekend_surcharge', v ?? 0)}
              help="Tillæg for rustvognskørsel i weekender og på helligdage"
              readOnly={readOnly}
            />
            <PriceField 
              label="Nattetillæg"
              value={data.driving_night_surcharge}
              onChange={(v) => updateField('driving_night_surcharge', v ?? 0)}
              help="Tillæg for rustvognskørsel om natten"
              readOnly={readOnly}
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Section: Pickup Surcharges */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Afhentningssted (tillæg)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PriceField 
            label="Fra eget hjem"
            value={data.pickup_home_surcharge}
            onChange={(v) => updateField('pickup_home_surcharge', v ?? 0)}
            readOnly={readOnly}
          />
          <PriceField 
            label="Fra hospital"
            value={data.pickup_hospital_surcharge}
            onChange={(v) => updateField('pickup_hospital_surcharge', v ?? 0)}
            readOnly={readOnly}
          />
          <PriceField 
            label="Fra institution/plejehjem"
            value={data.pickup_institution_surcharge}
            onChange={(v) => updateField('pickup_institution_surcharge', v ?? 0)}
            readOnly={readOnly}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section: Special Conditions */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Særlige forhold (størrelse/vægt)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <PriceField 
            label="Vægt over 120 kg (tillæg)"
            value={data.overweight_surcharge}
            onChange={(v) => updateField('overweight_surcharge', v ?? 0)}
            readOnly={readOnly}
          />
          <PriceField 
            label="Højde over 195 cm (tillæg)"
            value={data.tall_surcharge}
            onChange={(v) => updateField('tall_surcharge', v ?? 0)}
            help="XL kiste til personer over 195 cm"
            readOnly={readOnly}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section: Coffin & Urn - Only shown if NOT included in base */}
      <Accordion disabled={data.coffin_included_in_base && data.urn_included_in_base}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Kiste og urne
            {(data.coffin_included_in_base && data.urn_included_in_base) && (
              <Typography component="span" variant="caption" sx={{ ml: 1, color: 'success.main' }}>
                ✓ inkluderet i grundpris
              </Typography>
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {!data.coffin_included_in_base && !data.urn_included_in_base 
              ? "Kiste og urne er IKKE inkluderet i grundprisen – disse priser lægges oveni."
              : !data.coffin_included_in_base 
                ? "Kiste er IKKE inkluderet i grundprisen – denne pris lægges oveni."
                : "Urne er IKKE inkluderet i grundprisen – denne pris lægges oveni."
            }
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ved bisættelse bruges kisten til ceremonien (før kremering), og urnen til asken.
          </Typography>
          <PriceField 
            label="Kiste (billigste pris)"
            value={data.coffin_price}
            onChange={(v) => updateField('coffin_price', v ?? 0)}
            help="Denne pris lægges oveni grundpakken"
            readOnly={readOnly || data.coffin_included_in_base}
          />
          <PriceField 
            label="Urne (billigste pris)"
            value={data.urn_price}
            onChange={(v) => updateField('urn_price', v ?? 0)}
            help="Denne pris lægges oveni grundpakken (kun ved bisættelse)"
            readOnly={readOnly || data.urn_included_in_base}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section: Additional Fees */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Faste gebyrer
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sæt til 0 hvis inkluderet i grundpakken.
          </Typography>
          <PriceField 
            label="Bedemandshonorar (papirarbejde mv.)"
            value={data.honorar_fee}
            onChange={(v) => updateField('honorar_fee', v ?? 0)}
            readOnly={readOnly}
          />
          <PriceField 
            label="Kisteilægning (klargøring af afdøde)"
            value={data.preparation_fee}
            onChange={(v) => updateField('preparation_fee', v ?? 0)}
            readOnly={readOnly}
          />
          <PriceField 
            label="Hygiejne- og miljøtillæg"
            value={data.hygiene_fee}
            onChange={(v) => updateField('hygiene_fee', v ?? 0)}
            readOnly={readOnly}
          />
          <PriceField 
            label="Weekend/helligdagstillæg (ceremoni)"
            value={data.weekend_surcharge}
            onChange={(v) => updateField('weekend_surcharge', v ?? 0)}
            help="Tillæg for afholdelse af ceremoni i weekender og på helligdage"
            readOnly={readOnly}
          />
        </AccordionDetails>
      </Accordion>

      {/* Section: Package Description */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Hvad er inkluderet/ekskluderet
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Denne tekst vises til brugeren i prisberegneren.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Inkluderet i prisen"
            value={data.package_includes || ''}
            onChange={(e) => updateField('package_includes', e.target.value || null)}
            disabled={readOnly}
            placeholder="F.eks.: Inkl. honorar, kisteilægning, 2x15 km kørsel"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            label="Ikke inkluderet"
            value={data.package_excludes || ''}
            onChange={(e) => updateField('package_excludes', e.target.value || null)}
            disabled={readOnly}
            placeholder="F.eks.: Ekskl. kremering, kirkegårdsydelser, kørsel over 15 km"
          />
        </AccordionDetails>
      </Accordion>

      {/* Section: Boost Settings (Admin Only) */}
      {isAdmin && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Boost i prisberegner
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" sx={{ mb: 2 }}>
              Boost gør at denne bedemand vises højere i prisberegnerens resultater, også uden for deres serviceområde.
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  checked={data.boost_in_quotes}
                  onChange={(e) => updateField('boost_in_quotes', e.target.checked)}
                  disabled={readOnly}
                />
              }
              label="Vis altid i prisberegner (også uden for serviceområde)"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                Boost prioritet (højere = vises først)
              </Typography>
              <TextField
                size="small"
                type="number"
                value={data.boost_priority}
                onChange={(e) => updateField('boost_priority', Number(e.target.value) || 0)}
                disabled={readOnly}
                inputProps={{ min: 0, max: 100 }}
                sx={{ width: 120 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              0 = normal, 1-10 = boostet, 10+ = meget boostet
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  )
})

PrisberegnerForm.displayName = 'PrisberegnerForm'

export default PrisberegnerForm
